import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";

const PaymentSuccess = ({ user }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);
  const basePath = process.env.REACT_APP_BASE_URL || '';

  const getAuthHeaders = () => {
    const token = localStorage.getItem("jwtToken");
    const tokenType = localStorage.getItem("tokenType") || "Bearer";

    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `${tokenType} ${token}`;
    }

    return headers;
  };

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get("sessionId");

      if (!sessionId) {
        setStatus("error");
        setError("Invalid session ID");
        return;
      }

      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const endpoint = `${baseUrl}/api/payments/checkout-session/${sessionId}`;

        const response = await fetch(endpoint, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        if (response.status === 401) {
          navigate(`${basePath}/login`);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to verify payment: ${response.status}`);
        }

        const data = await response.json();
        setSessionData(data);

        if (data.paymentStatus === "paid") {
          setStatus("success");
        } else if (data.paymentStatus === "unpaid") {
          setStatus("pending");
        } else {
          setStatus("failed");
        }
      } catch (err) {
        console.error("Error verifying payment:", err);
        setStatus("error");
        setError(err.message);
      }
    };

    verifyPayment();
  }, [searchParams, navigate, basePath]);

  if (status === "loading") {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Verifying payment...</span>
        </Spinner>
        <p className="mt-3">Verifying your payment...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-5 text-center">
      {status === "success" && (
        <>
          <FontAwesomeIcon
            icon={faCheckCircle}
            size="3x"
            className="text-success mb-3"
          />
          <h1 className="text-success mb-3">Payment Successful!</h1>
          <Alert variant="success">
            <p>Your purchase has been completed successfully.</p>
            {sessionData && (
              <div className="mt-3 text-start">
                <p>
                  <strong>Amount:</strong> ${(sessionData.amountTotal / 100).toFixed(2)}{" "}
                  {sessionData.currency.toUpperCase()}
                </p>
                <p>
                  <strong>Order ID:</strong> {sessionData.id}
                </p>
              </div>
            )}
          </Alert>
          <button
            className="btn btn-primary mt-3"
            onClick={() => navigate(`${basePath}/dashboard`)}
          >
            Back to Dashboard
          </button>
        </>
      )}

      {status === "pending" && (
        <>
          <h1 className="text-warning mb-3">Payment Pending</h1>
          <Alert variant="warning">
            Your payment is being processed. Please wait...
          </Alert>
        </>
      )}

      {(status === "failed" || status === "error") && (
        <>
          <FontAwesomeIcon
            icon={faTimesCircle}
            size="3x"
            className="text-danger mb-3"
          />
          <h1 className="text-danger mb-3">Payment Failed</h1>
          <Alert variant="danger">
            {error || "Your payment could not be processed. Please try again."}
          </Alert>
          <button
            className="btn btn-primary mt-3"
            onClick={() => navigate(`${basePath}/one-time-payment`)}
          >
            Try Again
          </button>
        </>
      )}
    </Container>
  );
};

export default PaymentSuccess;